import { Box, Grid, Paper, Tooltip, Typography } from "@mui/material"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router"
import useHTMLElementSize from "@hooks/useHTMLElementSize"
import { findCategoryLevel, getOrderedCategories } from "@features/graph/GraphCategories.utils"
import GraphCategoriesNavigationBar from "@features/graph/GraphCategoriesNavigationBar"

const MAX_NUMBER_OF_COLUMNS = 4
const TILES_GAP_PX = 20
const MIN_TILE_HEIGHT_PX = 40

export type CategoriesTreeLevel = {
  groupId: string
  parentId: string | null
  label: string
  totalNodesCount: number
  color: string
  groups: CategoriesTreeLevel[]
}

export type CategoryWithHeight = CategoriesTreeLevel & { height: number }

type GraphCategoriesProps = {
  tree: CategoriesTreeLevel[]
  compact?: boolean
}

const GraphCategories = (props: GraphCategoriesProps) => {
  const [searchParams, setSearchParams] = useSearchParams({})
  const tilesContainerRef = useRef<HTMLDivElement>(null)
  const { height: tilesContainerHeight } = useHTMLElementSize(tilesContainerRef.current)
  const [groupsWithHeight, setGroupsWithHeight] = useState<CategoryWithHeight[]>([])

  const currentLevelId = searchParams.get("categoryId") ?? "root"
  const currentLevel = useMemo(
    () => findCategoryLevel(props.tree, currentLevelId),
    [props.tree, currentLevelId],
  )

  const totalNodesCountInGroups =
    currentLevel?.groups.reduce((sum, group) => sum + group.totalNodesCount, 0) ?? 0
  const totalNodesPerColumn = totalNodesCountInGroups / MAX_NUMBER_OF_COLUMNS

  const calculateGroupHeight = useCallback(
    (numberOfNodesInGroup: number) => {
      const height = tilesContainerHeight ?? tilesContainerRef.current?.clientHeight
      if (!height) return 0

      if (numberOfNodesInGroup >= totalNodesPerColumn) return height

      const ratio = numberOfNodesInGroup / totalNodesPerColumn

      return Math.max(Math.ceil(height * ratio), MIN_TILE_HEIGHT_PX)
    },
    [tilesContainerHeight, totalNodesPerColumn],
  )

  useEffect(() => {
    if (!currentLevel || !tilesContainerRef.current) return

    setGroupsWithHeight(
      currentLevel.groups.map((group) => ({
        ...group,
        height: calculateGroupHeight(group.totalNodesCount),
      })),
    )
  }, [calculateGroupHeight, currentLevel])

  if (!currentLevel) return <></>

  const columnHeight = tilesContainerHeight ?? tilesContainerRef.current?.clientHeight
  const orderedGroups = getOrderedCategories(groupsWithHeight, columnHeight, TILES_GAP_PX)

  const handleCategoryClick = (categoryId: string) => {
    setSearchParams((params) => {
      params.set("categoryId", categoryId)
      return params
    })
  }

  return (
    <Grid container sx={{ height: props.compact ? "auto" : "100%" }} direction="column">
      <Grid item xs="auto">
        <GraphCategoriesNavigationBar currentLevel={currentLevel} tree={props.tree} />
      </Grid>

      {orderedGroups.length === 0 && (
        <Grid item xs="auto">
          <Box
            role="status"
            sx={{
              px: 2,
              py: 1.5,
              border: "1px solid",
              borderColor: "info.main",
              borderRadius: 1,
              backgroundColor: "info.dark",
              color: "info.contrastText",
            }}
          >
            <Typography variant="h6">You've reached the lowest level.</Typography>
            <Typography>Select a node below or go back to the previous group.</Typography>
          </Box>
        </Grid>
      )}

      <Grid
        item
        xs
        sx={{
          overflow: "scroll",
          width: "100%",
          p: 1,
          // can't be conditionally rendered to work correctly with ref
          display: orderedGroups.length === 0 ? "none" : undefined,
        }}
        ref={tilesContainerRef}
        container
        columns={orderedGroups.length + 1}
        wrap="nowrap"
        gap={2}
      >
        {orderedGroups.map((groups, i) => (
          <Grid
            container
            direction="column"
            sx={{ height: "100%" }}
            key={i}
            gap={`${TILES_GAP_PX}px`}
            wrap="nowrap"
          >
            {groups.map((group) => (
              <Grid item key={group.groupId}>
                <Paper
                  data-testid={`group-container-in-graph-categories_${group.groupId}`}
                  elevation={5}
                  onClick={() => handleCategoryClick(group.groupId)}
                  sx={{
                    height: `${group.height}px`,
                    minHeight: "fit-content",
                    boxSizing: "border-box",
                    position: "relative",
                    textAlign: "center",
                    alignContent: "center",
                    backgroundColor: group.color,
                    p: "10px",
                    cursor: "pointer",
                    wordBreak: "break-word",
                    transition: "transform .3s ease",
                    fontWeight: "bold",
                    "&:hover": {
                      filter: "brightness(90%)",
                      transform: "scale(1.02)",
                    },
                  }}
                >
                  {group.label}
                  <Tooltip title="Number of nodes nested inside of this group">
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        top: 2,
                        left: 2,
                        fontSize: ".6rem",
                        fontWeight: "bold",
                      }}
                    >
                      {group.totalNodesCount}
                    </Typography>
                  </Tooltip>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ))}
      </Grid>
    </Grid>
  )
}

export default GraphCategories
